import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, IconButton, CircularProgress, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useNavigate } from 'react-router-dom';
import { transactionsService, PayoutRequest, PayoutRequestsSummary } from '../../services/transactions.service';
import { toast } from 'react-toastify';

interface PayoutRequestsProps {
  activeTab: 'payout-requests' | 'order-payments';
  setActiveTab: (tab: 'payout-requests' | 'order-payments') => void;
  tabs: Array<{ key: string; label: string; badgeCount: number }>;
  searchQuery: string;
}

const formatDate = (epochMs: number) =>
  new Date(epochMs).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const PayoutRequests: React.FC<PayoutRequestsProps> = ({ activeTab, setActiveTab, tabs, searchQuery }) => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<PayoutRequest[]>([]);
  const [summary, setSummary] = useState<PayoutRequestsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRequest, setSelectedRequest] = useState<PayoutRequest | null>(null);
  const [failDialogOpen, setFailDialogOpen] = useState(false);
  const [failureReason, setFailureReason] = useState('');
  const [paidDialogOpen, setPaidDialogOpen] = useState(false);
  const [bankReference, setBankReference] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'new' | 'paid' | 'failed'>('all');
  const [statusAnchorEl, setStatusAnchorEl] = useState<null | HTMLElement>(null);
  const ITEMS_PER_PAGE = 10;

  const fetchPayoutRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const statusParam = statusFilter !== 'all' ? statusFilter : undefined;
      const response = await transactionsService.getPayoutRequests(
        currentPage,
        ITEMS_PER_PAGE,
        undefined,
        statusParam
      );

      if (!response || !response.success || !response.data) {
        setError('Invalid response from server');
        return;
      }

      const serviceResponse = response.data;
      const mapped: PayoutRequest[] = (serviceResponse.data || []).map((item: any) => ({
        id: item.payoutRequestId,
        payoutRequestId: item.payoutRequestId,
        date: formatDate(item.createdAt),
        vendor: item.vendorName || item.vendorId,
        vendorId: item.vendorId,
        description: item.description || `Payout for ${item.orderCount || 0} completed orders`,
        amount: `QAR ${Number(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        grossAmount: item.grossAmount,
        feeAmount: item.feeAmount,
        orderCount: item.orderCount,
        status: item.status,
        bankReference: item.bankReference,
        periodStart: item.periodStart,
        periodEnd: item.periodEnd,
      }));

      setRequests(mapped);
      setSummary(serviceResponse.summary || null);
      setTotalPages(serviceResponse.pagination?.totalPages || 1);
      setTotalItems(serviceResponse.pagination?.totalItems || mapped.length);
    } catch (err) {
      console.error('Error fetching payout requests:', err);
      setError('Failed to load payout requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayoutRequests();
  }, [currentPage, statusFilter]);

  const filteredRequests = useMemo(() => {
    if (!searchQuery.trim()) return requests;
    const query = searchQuery.toLowerCase();
    return requests.filter(
      (request) =>
        request.date.toLowerCase().includes(query) ||
        request.vendor.toLowerCase().includes(query) ||
        request.description.toLowerCase().includes(query) ||
        request.amount.toLowerCase().includes(query) ||
        request.status.toLowerCase().includes(query) ||
        request.payoutRequestId.toLowerCase().includes(query)
    );
  }, [requests, searchQuery]);

  const displayTotalItems = searchQuery.trim() ? filteredRequests.length : totalItems;
  const displayTotalPages = searchQuery.trim() ? 1 : totalPages;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, request: PayoutRequest) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenPaidDialog = () => {
    setAnchorEl(null);
    setBankReference('');
    setPaidDialogOpen(true);
  };

  const handleMarkAsFailed = () => {
    setAnchorEl(null);
    setFailDialogOpen(true);
  };

  const handleFailDialogClose = () => {
    setFailDialogOpen(false);
    setFailureReason('');
    setSelectedRequest(null);
  };

  const handlePaidDialogClose = () => {
    setPaidDialogOpen(false);
    setBankReference('');
    setSelectedRequest(null);
  };

  const handleConfirmPaid = async () => {
    if (!selectedRequest?.payoutRequestId || !bankReference.trim()) {
      toast.error('Please provide a bank / transaction reference');
      return;
    }

    try {
      await transactionsService.markPayoutRequestPaid(
        selectedRequest.payoutRequestId,
        bankReference.trim()
      );
      toast.success('Payout request marked as paid');
      handlePaidDialogClose();
      fetchPayoutRequests();
    } catch (err) {
      console.error('Error marking payout as paid:', err);
      toast.error('Failed to mark payout request as paid');
    }
  };

  const handleConfirmFailed = async () => {
    if (!selectedRequest?.payoutRequestId || !failureReason.trim()) {
      toast.error('Please provide a failure reason');
      return;
    }

    try {
      await transactionsService.markPayoutRequestFailed(
        selectedRequest.payoutRequestId,
        failureReason.trim()
      );
      toast.success('Payout request marked as failed');
      handleFailDialogClose();
      fetchPayoutRequests();
    } catch (err) {
      console.error('Error marking payout as failed:', err);
      toast.error('Failed to mark payout request as failed');
    }
  };

  const getStatusConfig = (status: PayoutRequest['status']) => {
    const configs = {
      new: {
        label: 'New',
        backgroundColor: 'var(--palette-light-status-success-value-hex)',
        color: 'var(--palette-light-text-foreground-value-hex)',
      },
      paid: {
        label: 'Paid',
        backgroundColor: 'var(--palette-light-text-tertiary-value-hex)',
        color: 'var(--palette-light-text-foreground-value-hex)',
      },
      failed: {
        label: 'Failed',
        backgroundColor: 'var(--palette-light-status-danger-value-hex)',
        color: 'var(--palette-light-text-foreground-value-hex)',
      },
    };
    return configs[status];
  };

  const statusFilterLabel =
    statusFilter === 'all' ? 'Any status' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1);

  return (
    <>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        {selectedRequest?.status !== 'paid' && (
          <MenuItem onClick={handleOpenPaidDialog}>Mark as Paid</MenuItem>
        )}
        {selectedRequest?.status === 'new' && (
          <MenuItem onClick={handleMarkAsFailed}>Mark as Failed</MenuItem>
        )}
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (selectedRequest) navigate(`/transactions/payouts/${selectedRequest.payoutRequestId}`);
          }}
        >
          View details
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={statusAnchorEl}
        open={Boolean(statusAnchorEl)}
        onClose={() => setStatusAnchorEl(null)}
      >
        {(['all', 'new', 'paid', 'failed'] as const).map((value) => (
          <MenuItem
            key={value}
            onClick={() => {
              setStatusFilter(value);
              setCurrentPage(1);
              setStatusAnchorEl(null);
            }}
          >
            {value === 'all' ? 'Any status' : value.charAt(0).toUpperCase() + value.slice(1)}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={paidDialogOpen} onClose={handlePaidDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Payout as Paid</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Bank / transaction reference"
            type="text"
            fullWidth
            value={bankReference}
            onChange={(e) => setBankReference(e.target.value)}
            placeholder="e.g. bank transfer ID or transaction reference"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePaidDialogClose}>Cancel</Button>
          <Button onClick={handleConfirmPaid} variant="contained" color="primary">
            Mark as Paid
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={failDialogOpen} onClose={handleFailDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Payout as Failed</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Failure Reason"
            type="text"
            fullWidth
            multiline
            rows={3}
            value={failureReason}
            onChange={(e) => setFailureReason(e.target.value)}
            placeholder="Enter the reason for marking this payout as failed..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFailDialogClose}>Cancel</Button>
          <Button onClick={handleConfirmFailed} variant="contained" color="error">
            Mark as Failed
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px', flex: 1, overflow: 'hidden' }}>
        {summary && (
          <Box
            sx={{
              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              borderRadius: '16px',
              padding: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'stretch',
                alignItems: 'stretch',
                gap: 'var(--palette-light-4-value)',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '200px' }}>
                <Typography
                  sx={{
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    fontWeight: 700,
                    color: 'var(--palette-light-text-primary-value-hex)',
                  }}
                >
                  QAR {parseFloat(summary.totalAmount || '0').toLocaleString()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    color: 'var(--palette-light-text-primary-value-hex)',
                  }}
                >
                  Total payouts
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '200px' }}>
                <Typography
                  sx={{
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    fontWeight: 700,
                    color: 'var(--palette-light-text-primary-value-hex)',
                  }}
                >
                  QAR {parseFloat(summary.pendingAmount || '0').toLocaleString()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    color: 'var(--palette-light-text-primary-value-hex)',
                  }}
                >
                  Pending ({summary.newRequests || 0})
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: '200px' }}>
                <Typography
                  sx={{
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    fontWeight: 700,
                    color: 'var(--palette-light-text-primary-value-hex)',
                  }}
                >
                  QAR {parseFloat(summary.paidAmount || '0').toLocaleString()}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    color: 'var(--palette-light-text-primary-value-hex)',
                  }}
                >
                  Paid ({summary.paidRequests || 0})
                </Typography>
              </Box>
            </Box>
          </Box>
        )}

        {loading && requests.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {tabs.map((tab) => (
                  <Box
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key as typeof activeTab)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px 7px',
                      height: '39px',
                      backgroundColor:
                        tab.key === activeTab
                          ? 'var(--palette-light-brand-secondary-value-hex)'
                          : 'var(--palette-light-background-secondary-value-hex)',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.9 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 'var(--palette-light-font-size-default-value)',
                        color:
                          tab.key === activeTab
                            ? 'var(--palette-light-text-foreground-value-hex)'
                            : 'var(--palette-light-text-primary-value-hex)',
                      }}
                    >
                      {tab.label}
                    </Typography>
                    {tab.badgeCount > 0 && (
                      <Box
                        sx={{
                          backgroundColor:
                            tab.key === activeTab
                              ? 'var(--palette-light-text-foreground-value-hex)'
                              : 'var(--palette-light-status-success-value-hex)',
                          color:
                            tab.key === activeTab
                              ? 'var(--palette-light-text-primary-value-hex)'
                              : 'var(--palette-light-text-foreground-value-hex)',
                          fontSize: 'var(--palette-light-font-size-small-value)',
                          fontWeight: 700,
                          height: '20px',
                          minWidth: '20px',
                          borderRadius: '20px',
                          border:
                            tab.key === activeTab
                              ? 'none'
                              : '2px solid var(--palette-light-background-primary-value-hex)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '2px 6px 0',
                        }}
                      >
                        {tab.badgeCount}
                      </Box>
                    )}
                  </Box>
                ))}

                <Button
                  onClick={(e) => setStatusAnchorEl(e.currentTarget)}
                  sx={{
                    backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                    color: 'var(--palette-light-text-primary-value-hex)',
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    height: '39px',
                    padding: '10px 20px 7px',
                    borderRadius: '16px',
                    textTransform: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    '&:hover': {
                      backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                      opacity: 0.9,
                    },
                  }}
                >
                  {statusFilterLabel}
                  <KeyboardArrowDownIcon sx={{ fontSize: '20px' }} />
                </Button>
              </Box>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box
                sx={{
                  backgroundColor: 'var(--palette-light-background-primary-value-hex)',
                  width: '100%',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: '16px',
                    padding: '12px 0px',
                    borderBottom: '1px solid var(--palette-light-border-primary-value-hex)',
                    flexShrink: 0,
                  }}
                >
                  <Box sx={{ width: '90px' }}>
                    <Typography
                      sx={{
                        fontSize: 'var(--palette-light-font-size-small-value)',
                        color: 'var(--palette-light-text-secondary-value-hex)',
                      }}
                    >
                      Date/time
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 'var(--palette-light-font-size-small-value)',
                        color: 'var(--palette-light-text-secondary-value-hex)',
                      }}
                    >
                      Vendor
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontSize: 'var(--palette-light-font-size-small-value)',
                        color: 'var(--palette-light-text-secondary-value-hex)',
                      }}
                    >
                      Description
                    </Typography>
                  </Box>
                  <Box sx={{ width: '120px', display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography
                      sx={{
                        fontSize: 'var(--palette-light-font-size-small-value)',
                        color: 'var(--palette-light-text-secondary-value-hex)',
                      }}
                    >
                      Amount
                    </Typography>
                  </Box>
                  <Box sx={{ width: '120px' }} />
                  <Box sx={{ width: '56px' }} />
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'var(--palette-light-text-tertiary-value-hex)',
                      borderRadius: '4px',
                    },
                  }}
                >
                  {filteredRequests.length === 0 ? (
                    <Box sx={{ padding: '40px 0', textAlign: 'center' }}>
                      <Typography
                        sx={{
                          fontSize: 'var(--palette-light-font-size-default-value)',
                          color: 'var(--palette-light-text-secondary-value-hex)',
                        }}
                      >
                        No payout requests found
                      </Typography>
                    </Box>
                  ) : (
                    filteredRequests.map((request) => {
                      const statusConfig = getStatusConfig(request.status);
                      return (
                        <Box
                          key={request.id}
                          onClick={() => navigate(`/transactions/payouts/${request.payoutRequestId}`)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            padding: '8px 0px',
                            borderBottom: '1px solid var(--palette-light-border-primary-value-hex)',
                            minWidth: '800px',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
                            },
                          }}
                        >
                          <Box sx={{ width: '90px', flexShrink: 0 }}>
                            <Typography
                              sx={{
                                fontSize: 'var(--palette-light-font-size-small-value)',
                                color: 'var(--palette-light-text-primary-value-hex)',
                              }}
                            >
                              {request.date}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: 'var(--palette-light-font-size-default-value)',
                                color: 'var(--palette-light-brand-primary-value-hex)',
                              }}
                            >
                              {request.vendor}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontSize: 'var(--palette-light-font-size-default-value)',
                                color: 'var(--palette-light-text-primary-value-hex)',
                              }}
                            >
                              {request.description}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '120px',
                              display: 'flex',
                              justifyContent: 'flex-end',
                              flexShrink: 0,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 'var(--palette-light-font-size-default-value)',
                                color: 'var(--palette-light-text-primary-value-hex)',
                              }}
                            >
                              {request.amount}
                            </Typography>
                          </Box>
                          <Box sx={{ width: '120px', flexShrink: 0 }}>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                padding: 'var(--palette-light-3-value)',
                                height: 'var(--palette-light-10-value)',
                                backgroundColor: statusConfig.backgroundColor,
                                borderRadius: 'var(--palette-light-2-value)',
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 'var(--palette-light-font-size-small-value)',
                                  color: statusConfig.color,
                                }}
                              >
                                {statusConfig.label}
                              </Typography>
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              width: '56px',
                              height: '56px',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <IconButton
                              onClick={(e) => handleMenuOpen(e, request)}
                              sx={{ width: '56px', height: '56px', borderRadius: '16px' }}
                            >
                              <MoreHorizIcon sx={{ color: 'var(--palette-light-brand-primary-value-hex)' }} />
                            </IconButton>
                          </Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '24px',
                  mt: '24px',
                  flexShrink: 0,
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '16px',
                    padding: '4px',
                  }}
                >
                  {!searchQuery.trim() &&
                    Array.from({ length: Math.min(displayTotalPages, 3) }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        sx={{
                          backgroundColor: page === currentPage ? '#F4DBC5' : 'transparent',
                          borderRadius: page === currentPage ? '50%' : '6px',
                          minWidth: '56px',
                          width: '56px',
                          height: '56px',
                          fontSize: 'var(--palette-light-font-size-default-value)',
                          fontWeight: page === currentPage ? 700 : 400,
                          color:
                            page === currentPage
                              ? 'var(--palette-light-text-foreground-value-hex)'
                              : 'var(--palette-light-text-primary-value-hex)',
                          textTransform: 'none',
                          padding: 0,
                          '&:hover': {
                            backgroundColor: page === currentPage ? '#F4DBC5' : 'rgba(0, 0, 0, 0.04)',
                          },
                        }}
                      >
                        {page}
                      </Button>
                    ))}
                  {!searchQuery.trim() && currentPage < displayTotalPages && (
                    <Button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      sx={{
                        backgroundColor: 'transparent',
                        borderRadius: '6px',
                        minWidth: 'auto',
                        padding: '0 32px',
                        height: '56px',
                        fontSize: 'var(--palette-light-font-size-default-value)',
                        fontWeight: 400,
                        color: 'var(--palette-light-text-primary-value-hex)',
                        textTransform: 'none',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      }}
                    >
                      Next
                    </Button>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    color: 'var(--palette-light-text-secondary-value-hex)',
                  }}
                >
                  {searchQuery.trim()
                    ? `Showing ${displayTotalItems} search result${displayTotalItems !== 1 ? 's' : ''}`
                    : `Page ${currentPage} of ${displayTotalPages} (${displayTotalItems} total payouts)`}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default PayoutRequests;
